import { runCareTick } from '@/domain/care/orchestrator';
import { clean } from '@/domain/care/poop';
import { applyDiscipline } from '@/domain/care/discipline';
import { applyHealWithCooldown } from '@/domain/care/disease';
import { applyFood, canEatFood, type FoodId } from '@/domain/care/foods';
import {
  applyTemperamentFoodEffect,
  cleanHappinessDelta,
  wrongDisciplinePenalty,
} from '@/domain/care/temperament-effects';
import { normalizeCareProfile, recordCare } from '@/domain/care/profile';
import { applyToy, type ToyId } from '@/domain/care/toys';
import { deriveTemperament } from '@/domain/care/temperament';
import { isSleeping } from '@/domain/sleep';
import { computeStage } from '@/domain/lifecycle';
import { FAST_STAGE_DURATIONS, STAGE_DURATIONS } from '@/domain/care/constants';
import { useStore } from '@/infra/store';
import { eventBus } from '@/infra/event-bus/EventBus';
import { logger } from '@/infra/logger/Logger';
import { throttle } from '@/infra/util/debounce';
import { emitCareEvent } from './care-events';
import { notify } from './notification';
import { saveCurrentSnapshot } from './save';

export interface CareTickService {
  start(): void;
  stop(): void;
  tickNow(nowMs?: number): void;
  feed(foodId?: FoodId): void;
  play(toyId?: ToyId): void;
  clean(): void;
  discipline(): void;
  heal(): void;
  toggleLight(): void;
}

type RefuseReason = 'cooldown' | 'not-needed' | 'tantrum';

class CareTickServiceImpl implements CareTickService {
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private lastTickAt = Date.now();
  private autosave = throttle(() => {
    void saveCurrentSnapshot();
  }, 2_000);

  start(): void {
    if (this.intervalId !== null) return;
    this.lastTickAt = Date.now();
    this.intervalId = setInterval(() => {
      this.tickNow(Date.now());
    }, 1_000);
    logger.info('care-tick.started');
  }

  stop(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
      logger.info('care-tick.stopped');
    }
  }

  tickNow(nowMs: number = Date.now()): void {
    const dt = nowMs - this.lastTickAt;
    this.lastTickAt = nowMs;
    if (dt <= 0) return;
    const state = useStore.getState();
    const c = state.creature;
    if (!c || c.state.dead) return;

    const result = runCareTick({
      creature: c,
      dtMs: dt,
      nowMs,
      lightOn: state.lightOn,
      fastMode: state.settings.fastMode,
    });
    state.replaceCreature(result.creature);
    for (const ev of result.events) {
      emitCareEvent(c.id, ev);
      if (ev.kind === 'evolved') {
        notify({ key: `evolution.tier.${ev.tier}`, severity: 'info', params: { name: c.name } });
      }
    }
    this.autosave();
  }

  feed(foodId: FoodId = 'meal'): void {
    const s = useStore.getState();
    const c = s.creature;
    if (!c) return this.refuse('feed', 'blocked');
    const reason = this.canAct(c, false);
    if (reason !== 'ok') return this.refuse('feed', reason);
    if (!canEatFood(c.state, foodId)) return this.refuse('feed', 'mild', 'not-needed');
    const stateAfterFood = applyTemperamentFoodEffect(c, foodId, applyFood(c.state, foodId));
    const next = {
      ...c,
      state: stateAfterFood,
      careProfile: recordCare(normalizeCareProfile(c.careProfile), { kind: 'food', foodId }),
    };
    s.replaceCreature(next);
    s.setLastFeedAt(Date.now());
    eventBus.emit('creature.fed', { creatureId: c.id, foodId });
    this.autosave();
  }

  play(toyId: ToyId = 'ball'): void {
    const s = useStore.getState();
    const c = s.creature;
    if (!c) return this.refuse('play', 'blocked');
    const reason = this.canAct(c, false);
    if (reason !== 'ok') return this.refuse('play', reason);
    const profile = normalizeCareProfile(c.careProfile);
    const repeatCount = profile.lastToyId === toyId ? profile.consecutiveToyUses : 0;
    const next = {
      ...c,
      state: applyToy(c.state, toyId, deriveTemperament(c.genome), repeatCount),
      careProfile: recordCare(profile, { kind: 'play', toyId }),
    };
    s.replaceCreature(next);
    s.setLastPlayAt(Date.now());
    eventBus.emit('creature.played', { creatureId: c.id, toyId });
    this.autosave();
  }

  clean(): void {
    const s = useStore.getState();
    const c = s.creature;
    if (!c) return this.refuse('clean', 'blocked');
    const reason = this.canAct(c, true);
    if (reason !== 'ok') return this.refuse('clean', reason);
    if (c.state.poopCount === 0) return this.refuse('clean', 'mild', 'not-needed');
    const late = c.state.poopCount >= 3;
    const cleaned = clean({ poopCount: c.state.poopCount, lastPoopAt: c.state.lastPoopAt });
    const happinessBonus = cleanHappinessDelta(c, c.state.poopCount > 0);
    s.replaceCreature({
      ...c,
      state: { ...c.state, ...cleaned, happiness: Math.min(100, c.state.happiness + happinessBonus) },
      careProfile: recordCare(normalizeCareProfile(c.careProfile), { kind: 'clean', late }),
    });
    eventBus.emit('creature.cleaned', { creatureId: c.id });
    this.autosave();
  }

  discipline(): void {
    const s = useStore.getState();
    const c = s.creature;
    if (!c) return this.refuse('discipline', 'blocked');
    const reason = this.canAct(c, true);
    if (reason !== 'ok') return this.refuse('discipline', reason);
    if (!c.state.tantrum) {
      s.replaceCreature({
        ...c,
        state: {
          ...c.state,
          happiness: Math.max(0, c.state.happiness - wrongDisciplinePenalty(c)),
        },
        careProfile: recordCare(normalizeCareProfile(c.careProfile), { kind: 'discipline', correct: false }),
      });
      this.refuse('discipline', 'mild', 'not-needed');
      this.autosave();
      return;
    }
    const disc = applyDiscipline(
      {
        discipline: c.state.discipline,
        tantrum: c.state.tantrum,
        tantrumStartedAt: c.state.tantrumStartedAt,
        attentionLowSince: c.state.attentionLowSince,
      },
      Date.now(),
    );
    s.replaceCreature({
      ...c,
      state: { ...c.state, ...disc },
      careProfile: recordCare(normalizeCareProfile(c.careProfile), {
        kind: 'discipline',
        correct: true,
        resolvedTantrum: c.state.tantrum && !disc.tantrum,
      }),
    });
    eventBus.emit('creature.disciplined', { creatureId: c.id, correct: true });
    this.autosave();
  }

  heal(): void {
    const s = useStore.getState();
    const c = s.creature;
    if (!c || c.state.dead) return this.refuse('heal', 'blocked');
    const result = applyHealWithCooldown(
      {
        sick: c.state.sick,
        sickSince: c.state.sickSince,
        healDoses: c.state.healDoses,
        lastHealAt: c.state.lastHealAt,
        lowHealthSince: c.state.lowHealthSince,
      },
      Date.now(),
    );
    if (!result.accepted) {
      return this.refuse('heal', 'mild', 'not-needed');
    }
    s.replaceCreature({
      ...c,
      state: { ...c.state, ...result.state },
      careProfile: recordCare(normalizeCareProfile(c.careProfile), { kind: 'heal' }),
    });
    eventBus.emit('creature.healed', { creatureId: c.id });
    this.autosave();
  }

  toggleLight(): void {
    useStore.getState().toggleLight();
  }

  private canAct(c: NonNullable<ReturnType<typeof useStore.getState>['creature']>, allowTantrum: boolean): 'ok' | 'blocked' | 'tantrum' {
    if (c.state.dead) return 'blocked';
    const durations = useStore.getState().settings.fastMode ? FAST_STAGE_DURATIONS : STAGE_DURATIONS;
    const stage = computeStage(Date.now() - c.lifecycle.bornAt, durations);
    if (stage === 'egg') return 'blocked';
    if (isSleeping(Date.now())) return 'blocked';
    if (!allowTantrum && c.state.tantrum) return 'tantrum';
    return 'ok';
  }

  private refuse(
    action: 'feed' | 'play' | 'clean' | 'discipline' | 'heal',
    severity: 'blocked' | 'tantrum' | 'mild',
    reason?: RefuseReason,
  ): void {
    if (severity === 'blocked') return;
    const id = useStore.getState().creature?.id ?? '';
    const c = useStore.getState().creature;
    if (c) {
      useStore.getState().replaceCreature({
        ...c,
        careProfile: recordCare(normalizeCareProfile(c.careProfile), { kind: 'refusal' }),
      });
    }
    const payload: {
      creatureId: string;
      action: 'feed' | 'play' | 'clean' | 'discipline' | 'heal';
      severity: 'tantrum' | 'mild';
      reason?: RefuseReason;
    } = { creatureId: id, action, severity };
    const resolvedReason = reason ?? (severity === 'tantrum' ? 'tantrum' : undefined);
    if (resolvedReason) payload.reason = resolvedReason;
    eventBus.emit('creature.refused', payload);
  }
}

let _instance: CareTickService | null = null;

export function getCareTickService(): CareTickService {
  if (!_instance) _instance = new CareTickServiceImpl();
  return _instance;
}

export function resetCareTickServiceForTests(): void {
  _instance?.stop();
  _instance = null;
}
